import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  orderNumber: "",
  subject: "",
  message: "",
  privacyAccepted: false,
};

const subjectOptions = [
  "Frage zu einem Artikel",
  "Bestellung und Bezahlung",
  "Versand und Lieferung",
  "Retoure und Erstattung",
  "Material und Pflege",
  "Sonstiges Anliegen",
];

function Contact() {
  const { brandName, contact = {} } = siteConfig;
  const { email, phone, phoneHref, address, serviceHours } = contact;
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const successRef = useRef(null);
  const submitTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (submitTimerRef.current) {
        window.clearTimeout(submitTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (showSuccess) {
      successRef.current?.focus();
    }
  }, [showSuccess]);

  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((current) => {
      if (!current[name]) return current;
      const nextErrors = { ...current };
      delete nextErrors[name];
      return nextErrors;
    });
    setShowSuccess(false);
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^[+\d][\d\s()/.-]{5,}$/;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Bitte gib deinen Vornamen ein.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Bitte gib deinen Nachnamen ein.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Bitte gib deine E-Mail-Adresse ein.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Bitte prüfe deine E-Mail-Adresse.";
    }
    if (formData.phone.trim() && !phonePattern.test(formData.phone.trim())) {
      nextErrors.phone = "Bitte gib eine gültige Telefonnummer ein.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Bitte wähle ein Thema aus.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Bitte beschreibe dein Anliegen.";
    } else if (formData.message.trim().length < 20) {
      nextErrors.message = "Deine Nachricht sollte mindestens 20 Zeichen haben.";
    }
    if (!formData.privacyAccepted) {
      nextErrors.privacyAccepted =
        "Bitte bestätige, dass wir deine Angaben bearbeiten dürfen.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setShowSuccess(false);
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      window.requestAnimationFrame(() => {
        formRef.current?.querySelector("[aria-invalid='true']")?.focus();
      });
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    submitTimerRef.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
      submitTimerRef.current = null;
    }, 900);
  };

  const errorFor = (name) => errors[name];

  return (
    <main className="contact">
      <header className="contact__hero">
        <div className="contact__hero-inner">
          <nav className="contact__breadcrumb" aria-label="Brotkrümelnavigation">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Kontakt</span>
          </nav>
          <p className="contact__eyebrow">Service &amp; Beratung</p>
          <h1>Wir sind gerne für dich da.</h1>
          <p>
            Ob Passform, Material oder Bestellung: Das Serviceteam von {brandName}
            hilft persönlich und unkompliziert weiter.
          </p>
        </div>
      </header>

      <section className="contact__content" aria-labelledby="contact-form-title">
        <aside className="contact__details" aria-label="Kontaktmöglichkeiten">
          <p className="contact__kicker">Direkter Kontakt</p>
          <h2>Wie können wir helfen?</h2>

          <dl className="contact__detail-list">
            {phone && (
              <div>
                <dt>Telefon</dt>
                <dd>
                  <a href={`tel:${phoneHref || phone.replace(/[^+\d]/g, "")}`}>
                    {phone}
                  </a>
                </dd>
              </div>
            )}
            {email && (
              <div>
                <dt>E-Mail</dt>
                <dd>
                  <a href={`mailto:${email}`}>{email}</a>
                </dd>
              </div>
            )}
            {serviceHours && (
              <div>
                <dt>Servicezeiten</dt>
                <dd>{serviceHours}</dd>
              </div>
            )}
            {address && (
              <div>
                <dt>Postanschrift</dt>
                <dd>{address}</dd>
              </div>
            )}
          </dl>

          <div className="contact__quick-help">
            <h3>Schnell selbst nachsehen</h3>
            <Link to="/shipping">
              Versandinformationen <span aria-hidden="true">→</span>
            </Link>
            <Link to="/returns">
              Retoure &amp; Erstattung <span aria-hidden="true">→</span>
            </Link>
          </div>
        </aside>

        <div className="contact__form-panel">
          <div className="contact__form-heading">
            <p className="contact__kicker">Nachricht senden</p>
            <h2 id="contact-form-title">Schreib uns dein Anliegen.</h2>
            <p>Pflichtfelder sind mit einem Sternchen gekennzeichnet.</p>
          </div>

          {showSuccess && (
            <div
              className="contact__success"
              role="status"
              tabIndex="-1"
              ref={successRef}
            >
              <span aria-hidden="true">✓</span>
              <div>
                <h3>Vielen Dank für deine Nachricht.</h3>
                <p>Wir melden uns so bald wie möglich bei dir.</p>
              </div>
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="contact-first-name">Vorname *</label>
                <input
                  id="contact-first-name"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errorFor("firstName"))}
                  aria-describedby={
                    errorFor("firstName") ? "contact-first-name-error" : undefined
                  }
                />
                {errorFor("firstName") && (
                  <p id="contact-first-name-error" className="contact__error">
                    {errorFor("firstName")}
                  </p>
                )}
              </div>

              <div className="contact__field">
                <label htmlFor="contact-last-name">Nachname *</label>
                <input
                  id="contact-last-name"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errorFor("lastName"))}
                  aria-describedby={
                    errorFor("lastName") ? "contact-last-name-error" : undefined
                  }
                />
                {errorFor("lastName") && (
                  <p id="contact-last-name-error" className="contact__error">
                    {errorFor("lastName")}
                  </p>
                )}
              </div>
            </div>

            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="contact-email">E-Mail-Adresse *</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errorFor("email"))}
                  aria-describedby={
                    errorFor("email") ? "contact-email-error" : undefined
                  }
                />
                {errorFor("email") && (
                  <p id="contact-email-error" className="contact__error">
                    {errorFor("email")}
                  </p>
                )}
              </div>

              <div className="contact__field">
                <label htmlFor="contact-phone">Telefon (optional)</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errorFor("phone"))}
                  aria-describedby={
                    errorFor("phone") ? "contact-phone-error" : undefined
                  }
                />
                {errorFor("phone") && (
                  <p id="contact-phone-error" className="contact__error">
                    {errorFor("phone")}
                  </p>
                )}
              </div>
            </div>

            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="contact-order">Bestellnummer (optional)</label>
                <input
                  id="contact-order"
                  name="orderNumber"
                  type="text"
                  autoComplete="off"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                />
              </div>

              <div className="contact__field">
                <label htmlFor="contact-subject">Thema *</label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errorFor("subject"))}
                  aria-describedby={
                    errorFor("subject") ? "contact-subject-error" : undefined
                  }
                >
                  <option value="">Bitte auswählen</option>
                  {subjectOptions.map((subject) => (
                    <option value={subject} key={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
                {errorFor("subject") && (
                  <p id="contact-subject-error" className="contact__error">
                    {errorFor("subject")}
                  </p>
                )}
              </div>
            </div>

            <div className="contact__field">
              <label htmlFor="contact-message">Deine Nachricht *</label>
              <textarea
                id="contact-message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errorFor("message"))}
                aria-describedby={
                  errorFor("message") ? "contact-message-error" : undefined
                }
              />
              <div className="contact__field-meta">
                {errorFor("message") ? (
                  <p id="contact-message-error" className="contact__error">
                    {errorFor("message")}
                  </p>
                ) : (
                  <span>Mindestens 20 Zeichen</span>
                )}
                <span>{formData.message.length} Zeichen</span>
              </div>
            </div>

            <div className="contact__consent">
              <input
                id="contact-privacy"
                name="privacyAccepted"
                type="checkbox"
                checked={formData.privacyAccepted}
                onChange={handleInputChange}
                aria-invalid={Boolean(errorFor("privacyAccepted"))}
                aria-describedby={
                  errorFor("privacyAccepted")
                    ? "contact-privacy-error"
                    : undefined
                }
              />
              <label htmlFor="contact-privacy">
                Ich habe die <Link to="/privacy">Datenschutzhinweise</Link>
                gelesen und bin mit der Bearbeitung meiner Angaben zur
                Beantwortung der Anfrage einverstanden. *
              </label>
            </div>
            {errorFor("privacyAccepted") && (
              <p id="contact-privacy-error" className="contact__error">
                {errorFor("privacyAccepted")}
              </p>
            )}

            <button
              className="contact__submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Nachricht wird gesendet …" : "Nachricht senden"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
